import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { DeviceHistoryService } from '../../http/device-history-service';
import { DeviceType } from '../../types/device';
import { deviceLogRoutes } from '../../types/device-log';

const LOG_COUNT = 10;

const MONO_FONT = "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, 'Courier New', monospace";

const TERMINAL_COLORS = {
    bg: '#1E1E1E',
    bar: '#2D2D2D',
    text: '#D4D4D4',
    muted: '#8A8A8A',
    prefix: '#6A9955',
    key: '#9CDCFE',
    string: '#CE9178',
    number: '#B5CEA8',
    keyword: '#569CD6',
};

interface JsonParts {
    prefix: string;
    json: string;
    suffix: string;
}

/**
 * Выделяет JSON из строки лога и форматирует его с отступами.
 * Префикс до JSON (например, "send to Kafka:measurement >>>>") сохраняется.
 *
 * @param {string} text строка лога
 * @returns {JsonParts | null} части строки или null, если JSON не найден
 */
function tryFormatJson(text: string): JsonParts | null {
    const start = text.search(/[{\[]/);
    if (start < 0) return null;

    const end = Math.max(text.lastIndexOf('}'), text.lastIndexOf(']'));
    if (end <= start) return null;

    try {
        const parsed: unknown = JSON.parse(text.slice(start, end + 1));
        return {
            prefix: text.slice(0, start),
            json: JSON.stringify(parsed, null, 2),
            suffix: text.slice(end + 1),
        };
    } catch {
        return null;
    }
}

/**
 * Цвет строки лога без JSON: ошибки — красным, предупреждения — жёлтым.
 *
 * @param {string} line строка лога
 * @returns {string} цвет текста
 */
function plainLineColor(line: string): string {
    if (/error|ошибка|exception|failed/i.test(line)) return '#F48771';
    if (/warn|предупрежд/i.test(line)) return '#DCDCAA';
    return TERMINAL_COLORS.text;
}

/**
 * Отделяет префикс от объекта в формате util.inspect (`{ key: 'value', ... }`).
 *
 * @param {string} text строка лога
 * @returns {{ prefix: string; body: string } | null} префикс и объект или null
 */
function splitInspect(text: string): { prefix: string; body: string } | null {
    const start = text.search(/[{\[]/);
    if (start < 0) return null;

    const body = text.slice(start);
    // В util.inspect ключи выводятся без кавычек: `key: value`
    if (!/\b[A-Za-z_$][\w$]*\s*:/.test(body)) return null;

    return { prefix: text.slice(0, start), body };
}

const INSPECT_TOKEN_PATTERN =
    /('(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*")(\s*:)?|([A-Za-z_$][\w$]*)(\s*:)|\b(true|false|null|undefined|NaN|Infinity)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g;

/**
 * Подсвечивает объект в формате util.inspect (без приведения к JSON).
 *
 * @param {string} text объект в формате util.inspect
 * @returns {React.ReactNode[]} подсвеченные фрагменты
 */
function highlightInspect(text: string): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    INSPECT_TOKEN_PATTERN.lastIndex = 0;
    while ((match = INSPECT_TOKEN_PATTERN.exec(text)) !== null) {
        if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));

        const key = String(match.index);
        if (match[1] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: match[2] ? TERMINAL_COLORS.key : TERMINAL_COLORS.string }}>
                    {match[1]}
                </span>,
            );
            if (match[2] !== undefined) {
                nodes.push(
                    <span key={`${key}:`} style={{ color: TERMINAL_COLORS.muted }}>
                        {match[2]}
                    </span>,
                );
            }
        } else if (match[3] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.key }}>
                    {match[3]}
                </span>,
            );
            if (match[4] !== undefined) {
                nodes.push(
                    <span key={`${key}:`} style={{ color: TERMINAL_COLORS.muted }}>
                        {match[4]}
                    </span>,
                );
            }
        } else if (match[5] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.keyword }}>
                    {match[5]}
                </span>,
            );
        } else if (match[6] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.number }}>
                    {match[6]}
                </span>,
            );
        } else if (match[7] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.muted }}>
                    {match[7]}
                </span>,
            );
        }

        lastIndex = INSPECT_TOKEN_PATTERN.lastIndex;
    }

    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes;
}

const JSON_TOKEN_PATTERN =
    /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],])/g;

/**
 * Подсвечивает токены JSON цветами, как в терминале.
 *
 * @param {string} json отформатированный JSON
 * @returns {React.ReactNode[]} подсвеченные фрагменты
 */
function highlightJson(json: string): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    JSON_TOKEN_PATTERN.lastIndex = 0;
    while ((match = JSON_TOKEN_PATTERN.exec(json)) !== null) {
        if (match.index > lastIndex) nodes.push(json.slice(lastIndex, match.index));

        const key = String(match.index);
        if (match[1] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: match[2] ? TERMINAL_COLORS.key : TERMINAL_COLORS.string }}>
                    {match[1]}
                </span>,
            );
            if (match[2] !== undefined) {
                nodes.push(
                    <span key={`${key}:`} style={{ color: TERMINAL_COLORS.muted }}>
                        {match[2]}
                    </span>,
                );
            }
        } else if (match[3] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.number }}>
                    {match[3]}
                </span>,
            );
        } else if (match[4] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.keyword }}>
                    {match[4]}
                </span>,
            );
        } else if (match[5] !== undefined) {
            nodes.push(
                <span key={key} style={{ color: TERMINAL_COLORS.muted }}>
                    {match[5]}
                </span>,
            );
        }

        lastIndex = JSON_TOKEN_PATTERN.lastIndex;
    }

    if (lastIndex < json.length) nodes.push(json.slice(lastIndex));
    return nodes;
}

interface Props {
    deviceId: number;
    deviceType: DeviceType;
}

//=================================================================================================
export default function DeviceLogs({ deviceId, deviceType }: Props) {
    const sources = useMemo(() => deviceLogRoutes(deviceType), [deviceType]);

    const [selectedSource, setSelectedSource] = useState<string>();
    const [logs, setLogs] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

    // Если выбранный сервис недоступен для текущего типа устройства, показываем первый доступный
    const source = selectedSource && sources.includes(selectedSource) ? selectedSource : sources[0];

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setError(null);
        setLogs([]);
        setCollapsed(new Set());

        DeviceHistoryService.getLogs(deviceId, source, LOG_COUNT)
            .then((res) => {
                if (!cancelled) setLogs(res.data);
            })
            .catch(() => {
                if (!cancelled) setError('Не удалось загрузить логи устройства');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [deviceId, source, reloadKey]);

    const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

    const toggleLog = useCallback((idx: number) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    }, []);

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="h6" sx={{ flexShrink: 0 }}>
                    Логи устройства
                </Typography>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="device-logs-source">Сервис</InputLabel>
                    <Select
                        labelId="device-logs-source"
                        label="Сервис"
                        value={source}
                        onChange={(e) => setSelectedSource(String(e.target.value))}
                    >
                        {sources.map((route) => (
                            <MenuItem key={route} value={route}>
                                {route}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{ flex: 1 }} />

                <Tooltip title="Обновить">
                    <span>
                        <IconButton onClick={refresh} disabled={loading} size="small">
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: TERMINAL_COLORS.bar,
                    }}
                >
                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: 12, color: TERMINAL_COLORS.muted }}>
                        {source}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: 11, color: '#6E6E6E' }}>
                        tail {LOG_COUNT}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        position: 'relative',
                        bgcolor: TERMINAL_COLORS.bg,
                        color: TERMINAL_COLORS.text,
                        p: 0.5,
                        minHeight: 120,
                        maxHeight: 360,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': { width: 8, height: 8 },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: 4,
                        },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
                    }}
                >
                    {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}

                    {!loading && !error && logs.length === 0 && (
                        <Typography sx={{ fontFamily: MONO_FONT, fontSize: 12, color: TERMINAL_COLORS.muted, p: 1 }}>
                            Записей нет
                        </Typography>
                    )}

                    {logs.map((line, idx) => {
                        const json = tryFormatJson(line);
                        const inspect = json ? null : splitInspect(line);
                        const isCollapsed = collapsed.has(idx);

                        return (
                            <Box
                                key={idx}
                                component="pre"
                                onClick={json ? () => toggleLog(idx) : undefined}
                                title={json ? (isCollapsed ? 'Показать JSON' : 'Свернуть JSON') : undefined}
                                sx={{
                                    m: 0,
                                    px: 1,
                                    py: 0.75,
                                    fontFamily: MONO_FONT,
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: TERMINAL_COLORS.text,
                                    cursor: json ? 'pointer' : 'default',
                                    '& + &': { borderTop: '1px solid rgba(255, 255, 255, 0.06)' },
                                    '&:hover': {
                                        bgcolor: json ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
                                    },
                                }}
                            >
                                {json && !isCollapsed ? (
                                    <>
                                        {json.prefix && (
                                            <span style={{ color: TERMINAL_COLORS.prefix }}>
                                                {json.prefix}
                                                {'\n'}
                                            </span>
                                        )}
                                        {highlightJson(json.json)}
                                        {json.suffix}
                                    </>
                                ) : inspect ? (
                                    <>
                                        {inspect.prefix && (
                                            <span style={{ color: TERMINAL_COLORS.prefix }}>{inspect.prefix}</span>
                                        )}
                                        {highlightInspect(inspect.body)}
                                    </>
                                ) : (
                                    <span style={{ color: plainLineColor(line) }}>{line}</span>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
